import type { ItemPermissionAction } from "./buildItemPermissionBulkRequests";

export type ItemPermissionFlags = {
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canDelegate: boolean;
};

export type CollectionAclState = {
  canUpdateCollection: boolean;
  updatePermissionLoading: boolean;
  canDeleteCollection: boolean;
  deletePermissionLoading: boolean;
  canDelegateCollection: boolean;
  delegateCollectionPermissionLoading: boolean;
};

const EMPTY_ITEM_FLAGS: ItemPermissionFlags = {
  canRead: false,
  canUpdate: false,
  canDelete: false,
  canDelegate: false,
};

/** Which per-item ACL actions to bulk-check (collection grants skip item checks). */
export function deriveItemPermissionActions(
  collection: CollectionAclState
): ItemPermissionAction[] {
  const actions: ItemPermissionAction[] = ["read"];
  if (!collection.updatePermissionLoading && !collection.canUpdateCollection) {
    actions.push("update");
  }
  if (!collection.deletePermissionLoading && !collection.canDeleteCollection) {
    actions.push("delete");
  }
  if (!collection.delegateCollectionPermissionLoading && !collection.canDelegateCollection) {
    actions.push("delegate");
  }
  return actions;
}

export function mergeItemPermissionFlags(
  base: ItemPermissionFlags,
  collection: CollectionAclState
): ItemPermissionFlags {
  return {
    canRead: base.canRead,
    canUpdate:
      base.canUpdate ||
      (!collection.updatePermissionLoading && collection.canUpdateCollection),
    canDelete:
      base.canDelete ||
      (!collection.deletePermissionLoading && collection.canDeleteCollection),
    canDelegate:
      base.canDelegate ||
      (!collection.delegateCollectionPermissionLoading && collection.canDelegateCollection),
  };
}

export function buildItemPermissionsById(
  itemPermissionConfig: {
    indexMap: Map<string, number>;
    actionsPerItem: ItemPermissionAction[];
  },
  itemPermissionResults: { allowed: boolean }[]
): Record<string, ItemPermissionFlags> {
  const permissions: Record<string, ItemPermissionFlags> = {};
  const { actionsPerItem } = itemPermissionConfig;

  itemPermissionConfig.indexMap.forEach((startIndex, uid) => {
    const flags: ItemPermissionFlags = {
      canRead: false,
      canUpdate: false,
      canDelete: false,
      canDelegate: false,
    };
    actionsPerItem.forEach((action, offset) => {
      const allowed = itemPermissionResults[startIndex + offset]?.allowed ?? false;
      if (action === "read") flags.canRead = allowed;
      if (action === "update") flags.canUpdate = allowed;
      if (action === "delete") flags.canDelete = allowed;
      if (action === "delegate") flags.canDelegate = allowed;
    });
    permissions[uid] = flags;
  });

  return permissions;
}

/**
 * True while the UI must wait for an item-level ACL result for this action.
 * Collection-level grants resolve immediately without waiting on the item bulk check.
 */
export function isItemActionPermissionPending(
  action: ItemPermissionAction,
  itemPermissionsLoading: boolean,
  collection: CollectionAclState,
  actionsCheckedPerItem: ItemPermissionAction[]
): boolean {
  if (!itemPermissionsLoading) {
    return false;
  }
  if (!actionsCheckedPerItem.includes(action)) {
    return false;
  }
  if (action === "update" && collection.canUpdateCollection && !collection.updatePermissionLoading) {
    return false;
  }
  if (action === "delete" && collection.canDeleteCollection && !collection.deletePermissionLoading) {
    return false;
  }
  if (action === "delegate" && collection.canDelegateCollection && !collection.delegateCollectionPermissionLoading) {
    return false;
  }
  return true;
}

export function createGetItemPermissionFlags(
  itemPermissionsById: Record<string, ItemPermissionFlags>,
  collection: CollectionAclState
): (uid?: string) => ItemPermissionFlags {
  return (uid?: string) => {
    if (!uid) {
      return EMPTY_ITEM_FLAGS;
    }
    const base = itemPermissionsById[uid] ?? EMPTY_ITEM_FLAGS;
    return mergeItemPermissionFlags(base, collection);
  };
}

export function createIsItemPermissionPending(
  itemPermissionsLoading: boolean,
  collection: CollectionAclState,
  actionsCheckedPerItem: ItemPermissionAction[]
): (action: ItemPermissionAction) => boolean {
  return (action) =>
    isItemActionPermissionPending(
      action,
      itemPermissionsLoading,
      collection,
      actionsCheckedPerItem
    );
}
